import React, { PureComponent } from 'react';
import memoizeOne from 'memoize-one';
import NetworkMonitoringMap from 'app/components/dashboard/network/NetworkMonitoringMap';
import {
    DEFAULT_MEASURES_SETTINGS,
    MeasuresSettings,
} from 'app/components/dashboard/detailed/measures/MeasuresSettings';
import { DEFAULT_FL_FILTER_RANGE } from 'app/components/dashboard/network/AbstractMapView';
import MapUtils from 'app/components/map/util/MapUtils';
import { NMUI4DContext, NMUI4DContextProps } from 'app/common/context/NMUI4DContext';
import { ProfileSegmentHighlight } from 'app/components/map/model/MapModel';

import {
    AccDashboardData,
    ConfiguredAirspace,
    DashboardData,
    DashboardDataRequest,
    DashboardFilteredData,
    Entity,
    ExtendedMonitoredTv,
    FilteredFlightExtraData,
    MonitoredMcdmMeasure,
    MonitoredMeasure,
    NetworkDashboardData,
    NetworkMapConvection,
    OverlappingFilter,
    SOL49_MAP_FLIGHTS_DATA_REQUEST_DTO_CLAZZ_VALUE,
} from '../model/SOL49';
import {
    ConfigData,
    CountType,
    FlightDto,
    FlightId,
    FlightLevelBand,
    McdmState,
    TimePeriod,
    TrafficType,
} from '../model/CORE';
import { FilterComponent } from '../components/dashboard/filtering/FilterComponent';
import {
    DEFAULT_TV_FLIGHTS_SETTINGS,
    TvFlightsFilterSettings,
    TvFlightsFilterSettingsButton,
} from '../components/dashboard/filtering/settings/TvFlightsFilterSettings';
import {
    DEFAULT_FLIGHTS_TV_SETTINGS,
    FlightsTvFilterSettings,
    FlightsTvFilterSettingsButton,
} from '../components/dashboard/filtering/settings/FlightsTvFilterSettings';
import { DEFAULT_MAP_SETTINGS, MapSettings } from '../components/dashboard/network/map/MapSettings';
import {
    DEFAULT_FLIGHT_LIST_FILTER_SETTINGS,
    FlightListFilterSettings,
} from '../components/dashboard/detailed/flights/FlightsSettings';
import AppContext from '../common/context/AppContext';
import { ServiceFactory } from '../service/ServiceFactory';
import {
    CoordinationSummaryUpdateEvent,
    DashboardSettingsUpdateEvent,
    MonitoringPeriodSettingsChange,
    PartialDashboardDataUpdateEvent,
    UpdateFlightSelection,
    UpdateTvSelection,
} from '../common/eventbus/Events';
import Utils from '../common/util/Utils';
import { TableUtils } from '../common/util/TableUtils';
import KeyUtils from '../common/util/KeyUtils';
import FlightUtils from '../common/util/FlightUtils';
import { Flex } from '../components/common/Flex';
import { SvgCommonDefs } from '../components/dashboard/network/flows/svg/SvgCommonDefs';
import { ClearButton } from '../components/dashboard/filtering/ClearButton';
import { Flights } from '../components/dashboard/detailed/flights/Flights';
import { DashboardModal } from '../components/panels/DashboardModal';
import {
    FilterButtonDirection,
    OverlappingFilterButtonDetail,
    OverlappingFilterUtils,
} from '../common/util/OverlappingFilterUtils';
import { FmpNetworkMonitoring } from '../components/dashboard/network/FmpNetworkMonitoring';
import { DashboardUtils } from './DashboardUtils';
import { DashboardSettings } from '../components/panels/settings/SettingsPanel';
import TrafficCountUtils from '../common/util/TrafficCountUtils';
import { Measures } from '../components/dashboard/detailed/measures/Measures';
import { FlexTabs } from '../components/common/FlexTabs';
import { EntityType } from '../model/client/SOL49';
import { DashboardFlightUtils } from './DashboardFlightUtils';
import './Dashboard.less';

interface FmpDashboardProps {
    configData: ConfigData;
}

interface FmpDashboardState {
    systemInfo?: string;
    logoutUrl?: string;
    settings: DashboardSettings;
    dashboardData?: DashboardData;
    filteredData?: DashboardFilteredData;
    currentTime: Date;
    monitoredPeriod: TimePeriod;
    globalTextFilter?: string;
    selectedPeriodByTv: Map<string, Array<TimePeriod>>;
    selectedFlights: Array<FlightDto>;
    selectedFlightsOccupancyPeriods: { [key: string]: Map<FlightId, TimePeriod> };
    selectedMeasures: Array<MonitoredMeasure>;
    tvFlightsFilterButtonDetails: OverlappingFilterButtonDetail<TvFlightsFilterSettings, FlightsTvFilterSettings>;
    mapSettings: MapSettings;
    flightListFilterSettings: FlightListFilterSettings;
    convectionData: NetworkMapConvection;
    globalFilterEnabled: boolean;
    configuredAirspaces?: Array<ConfiguredAirspace>;
    activeTab: string;
    countType: CountType;
    measureSettings: MeasuresSettings;
}

export class FmpDashboard extends PureComponent<FmpDashboardProps, FmpDashboardState> {
    refreshingNetworkMonitorData: boolean = false;
    state = {
        settings: AppContext.dashboardSettings,
        currentTime: AppContext.getCurrentTime(),
        monitoredPeriod: AppContext.getMonitoringPeriod(),
        selectedPeriodByTv: new Map(),
        selectedFlights: [],
        selectedMeasures: [],
        tvFlightsFilterButtonDetails: new OverlappingFilterButtonDetail(
            Entity.TV,
            Entity.FLIGHT,
            DEFAULT_TV_FLIGHTS_SETTINGS,
            DEFAULT_FLIGHTS_TV_SETTINGS,
        ),
        mapSettings: DEFAULT_MAP_SETTINGS,
        flightListFilterSettings: DEFAULT_FLIGHT_LIST_FILTER_SETTINGS,
        convectionData: null,
        // convectionAirspaceDef: null,
        globalFilterEnabled: true,
        configuredAirspaces: null,
        activeTab: 'map',
        countType: CountType.ENTRY,
        measureSettings: DEFAULT_MEASURES_SETTINGS,
    } as FmpDashboardState;

    public static contextType = NMUI4DContext;
    private nmui4dContext?: NMUI4DContextProps;

    private flightUtils = new DashboardFlightUtils();
    private display = CountType.ENTRY;
    private highlightedSegments = new Map<FlightId, ProfileSegmentHighlight[]>();
    private mapFlightDataWithTvLoads = new Map<FlightId, ProfileSegmentHighlight[]>();
    private enrichedFlights: FlightDto[] = [];

    private flightLevelFilter: FlightLevelBand = DEFAULT_FL_FILTER_RANGE;
    private mapRefTimePeriod: TimePeriod;
    private convectionCacheKey?: {
        networkForecastLastUpdate: Date;
        flightLevelFilter: FlightLevelBand;
        mapRefTimePeriod: TimePeriod;
        mapSettings: MapSettings;
    };

    public constructor(props) {
        super(props);
        this.initEventHandlers();

        this.mapRefTimePeriod = { from: AppContext.getCurrentTime(), to: AppContext.getMonitoringPeriod().to };
    }

    componentDidMount(): void {
        this.nmui4dContext = this.context as NMUI4DContextProps;

        this.initialise();
    }

    componentDidUpdate(prevProps) {
        if (this.props?.configData !== prevProps?.configData) {
            this.initialise();
        }
    }

    initEventHandlers = () => {
        AppContext.eventBus.register(MonitoringPeriodSettingsChange.NAME, (event: MonitoringPeriodSettingsChange) => {
            AppContext.setMonitoringPeriodSettings(event.monitoringPeriodSettings);
            this.refreshDashboardData(this.state.dashboardData.version);
        });
        AppContext.eventBus.register(DashboardSettingsUpdateEvent.NAME, (event: DashboardSettingsUpdateEvent) => {
            AppContext.setDashboardSettings(event.dashboardSettings);
            const wcmChanged = this.state.settings.capacityModel !== event.dashboardSettings.capacityModel;
            this.setState({ settings: event.dashboardSettings }, () => {
                if (wcmChanged) {
                    this.refreshDashboardData(this.state.dashboardData.version);
                }
            });
        });
        AppContext.eventBus.register(UpdateFlightSelection.NAME, (event: UpdateFlightSelection) => {
            const selectedFlights =
                event.action === 'replace'
                    ? event.selection
                    : Utils.copyAndAddAllIfNeeded(this.state.selectedFlights, event.selection);
            this.handleFlightSelectionChange(selectedFlights);
        });
        AppContext.eventBus.register(UpdateTvSelection.NAME, (event: UpdateTvSelection) => {
            const selectedTvs =
                event.action === 'replace'
                    ? (event.selection as Array<ExtendedMonitoredTv>)
                    : Utils.copyAndAddAllIfNeeded(this.getSelectedTvs(), event.selection as Array<ExtendedMonitoredTv>);
            this.handleTVSelectionChange(selectedTvs);
        });
        AppContext.eventBus.register(CoordinationSummaryUpdateEvent.NAME, (event: CoordinationSummaryUpdateEvent) => {
            if (Utils.isNotExists(this.state.dashboardData?.coordinationSummaries)) return;

            let newSummaries;
            if (this.state.dashboardData.coordinationSummaries.find(c => c.id === event.coordinationSummary.id)) {
                newSummaries = this.state.dashboardData.coordinationSummaries.map(c =>
                    c.id === event.coordinationSummary.id ? event.coordinationSummary : c,
                );
            } else {
                newSummaries = [...this.state.dashboardData.coordinationSummaries, event.coordinationSummary];
            }
            this.setState({
                dashboardData: { ...this.state.dashboardData, coordinationSummaries: newSummaries },
            });
        });
        AppContext.eventBus.register(PartialDashboardDataUpdateEvent.NAME, (event: PartialDashboardDataUpdateEvent) => {
            if (event.entityType === EntityType.MEASURE_PROPOSAL) {
                ServiceFactory.sol49Service()
                    .getMonitoredMcdmMeasures(this.state.monitoredPeriod)
                    .then(reply =>
                        this.setState({
                            dashboardData: { ...this.state.dashboardData, monitoredMcdmMeasures: reply.data },
                        }),
                    )
                    .catch(reason => console.log('getMonitoredMcdmMeasures failed:', reason.message));
            }
        });
    };

    initialise = () => {
        if (this.props.configData) {
            this.checkDataVersion();
            if (
                new URLSearchParams(window.location.search).get('dataset') === 'static' ||
                new URLSearchParams(window.location.search).get('record-data') === '1'
            ) {
                return; // This is just noise when using static data or recording.
            }

            if (
                new URLSearchParams(window.location.search).get('dataset') === 'static' ||
                new URLSearchParams(window.location.search).get('record-data') === '1'
            ) {
                return; // This is just noise when using static data or recording.
            }

            setInterval(() => {
                // Check if new data is available every 10s
                this.checkDataVersion();
            }, 10000);
        }
    };

    private updateHighlightedSegments(
        occupancyPeriods?: { [key: string]: Map<FlightId, TimePeriod> },
        monitoredTvs?: ExtendedMonitoredTv[],
    ) {
        if (!monitoredTvs) {
            monitoredTvs = this.getMonitoredTvList(this.state.dashboardData);
        }

        this.highlightedSegments = this.flightUtils.getHighlightedSegments(occupancyPeriods, monitoredTvs);
    }

    private sendFlights(flights?: FlightDto[]) {
        void this.nmui4dContext?.unityRequests.sendFlightsToUnityAsync(
            flights ?? this.state.selectedFlights,
            this.flightUtils.mergeMap(this.highlightedSegments, this.mapFlightDataWithTvLoads),
        );
    }

    private updateMapFlightData() {
        this.mapFlightDataWithTvLoads = this.flightUtils.computeMapFlightDataWithTvLoads(
            this.enrichedFlights,
            this.display,
        );

        this.sendFlights(this.enrichedFlights);
    }

    private async enrichFlightsAsync(flights: FlightDto[]) {
        this.enrichedFlights = await this.nmui4dContext?.flightEnricher.enrichAsync(
            flights,
            this.state.dashboardData?.version,
        );
    }

    private updateFlights(
        flights: FlightDto[],
        occupancyPeriods?: { [key: string]: Map<FlightId, TimePeriod> },
        monitoredTvs?: ExtendedMonitoredTv[],
    ) {
        this.updateHighlightedSegments(occupancyPeriods, monitoredTvs);

        this.enrichFlightsAsync(flights)
            .catch(reason => {
                this.nmui4dContext?.logger.error('Failed to retrieve flight data', reason);
            })
            .finally(() => {
                this.updateMapFlightData();
            });
    }

    private onDisplayChanged = (newDisplay: CountType) => {
        this.display = newDisplay;
        this.updateMapFlightData();
    };

    private updateConvections(mapSettings?: MapSettings) {
        if (!mapSettings) {
            mapSettings = this.state.mapSettings;
        }

        if (mapSettings?.showConvection) {
            if (
                this.convectionCacheKey &&
                this.convectionCacheKey.networkForecastLastUpdate ===
                    this.state.convectionData?.networkForecast?.lastUpdate &&
                this.convectionCacheKey.flightLevelFilter === this.flightLevelFilter &&
                this.convectionCacheKey.mapRefTimePeriod === this.mapRefTimePeriod &&
                this.convectionCacheKey.mapSettings === mapSettings
            ) {
                // NOTE(bjarne): We already sent these convections.
                return;
            }

            this.convectionCacheKey = {
                networkForecastLastUpdate: this.state.convectionData?.networkForecast?.lastUpdate,
                flightLevelFilter: this.flightLevelFilter,
                mapRefTimePeriod: this.mapRefTimePeriod,
                mapSettings: mapSettings,
            };

            const convectionMap = MapUtils.getConvectionMap(
                this.state.convectionData?.networkForecast,
                this.state.convectionData?.airspaceDefinition,
                this.mapRefTimePeriod,
            );
            this.nmui4dContext?.unityRequests.sendConvectionsToUnity(
                convectionMap ?? [],
                mapSettings?.showConvectionTvs,
                this.flightLevelFilter,
                this.mapRefTimePeriod,
                mapSettings?.hideConvectionWithNoImpact,
            );
        } else {
            if (this.convectionCacheKey === undefined) {
                // NOTE(bjarne): We already sent these (empty) convections.
                return;
            }

            this.convectionCacheKey = undefined;

            this.nmui4dContext?.unityRequests.sendConvectionsToUnity(
                [],
                mapSettings?.showConvectionTvs,
                this.flightLevelFilter,
                this.mapRefTimePeriod,
                mapSettings?.hideConvectionWithNoImpact,
            );
        }
    }

    private onFlightLevelFilterChanged = (flRange: FlightLevelBand) => {
        this.flightLevelFilter = flRange;
        this.updateConvections();
    };

    private onMapRefTimePeriodChanged = (newPeriod: TimePeriod) => {
        this.mapRefTimePeriod = newPeriod;
        this.updateConvections();
    };

    checkDataVersion = () => {
        // Prevent any additional request to the server until a reply has been received
        if (this.refreshingNetworkMonitorData) return;

        ServiceFactory.sol49Service()
            .getDataVersion(AppContext.getDataset().type)
            .then(reply => {
                if (reply.data !== this.state?.dashboardData?.version) {
                    this.refreshDashboardData(reply.data);
                }
            })
            .catch(reason => {
                console.log('getDataVersion failed:', reason.message);
            });
    };

    getMonitoredTvList = (dashboardData: DashboardData) => {
        return dashboardData?.monitoredTvList || [];
    };

    refreshDashboardData = (version: number) => {
        const request: DashboardDataRequest = {
            version: version,
            overlappingFilter: this.state.globalFilterEnabled ? this.getOverlappingFilter() : undefined,
            capacityModel: AppContext.getCapacityModel(),
            monitoredPeriod: AppContext.getMonitoringPeriod(),
            //if show map convection then include the map convection request to be returned
            mapConvectionRequest: this.state.mapSettings.showConvection
                ? { minLevel: this.state.mapSettings.convectionRiskLevel }
                : undefined,
        };
        const filteredData = this.state?.filteredData;
        this.refreshingNetworkMonitorData = true;
        ServiceFactory.sol49Service()
            .getDashboardData(request)
            .then(reply => {
                const data = reply.data;
                if (filteredData !== this.state?.filteredData) {
                    console.log('Filtered data has changed since the request was sent');
                    data.filteredData = this.state?.filteredData;
                }

                const selectedFlights = TableUtils.getUpdatedMultipleSelection(
                    this.state.selectedFlights,
                    reply.data.dashboardData?.flights,
                    KeyUtils.FLIGHT_KEY_PROVIDER,
                );
                this.updateFlights(
                    selectedFlights,
                    this.state.selectedFlightsOccupancyPeriods,
                    this.getMonitoredTvList(data.dashboardData),
                );

                // TODO: Adapt filters in regard of updated selection (If selected elements are no longer part of the data, some filters should be removed)
                this.setState(
                    {
                        currentTime: AppContext.getCurrentTime(),
                        monitoredPeriod: data.dashboardData.period,
                        dashboardData: data.dashboardData,
                        filteredData: data.filteredData,
                        //selectedTvs: TableUtils.getUpdatedMultipleSelection(this.state.selectedTvs, this.getMonitoredTvList(reply.data.dashboardData), KeyUtils.MONITORED_TV_KEY_PROVIDER),
                        selectedFlights: selectedFlights,
                        convectionData: data.mapConvection,
                        // convectionAirspaceDef: data.mapConvection?.airspaceDefinition,
                        configuredAirspaces: (reply.data.dashboardData as AccDashboardData).configuredAirspaces,
                    },
                    () => this.updateConvections(),
                );
            })
            .catch(reason => {
                //AppContext.errorNotifier.notifyCatch(reason.message)
            })
            .finally(() => {
                this.refreshingNetworkMonitorData = false;
            });
    };

    computeSelectedFlightsOccupancyPeriods = (flightsExtraData: Array<FilteredFlightExtraData>) => {
        let occupancyPeriods: { [key: string]: Map<FlightId, TimePeriod> };
        if (this.state?.selectedFlights) {
            const selectedFlightsIds = this.state?.selectedFlights.map(flight => flight.flightId);
            if (selectedFlightsIds?.length && flightsExtraData) {
                occupancyPeriods = {};
                flightsExtraData.forEach(extraData => {
                    // Ensure that returned occupancies only concern selected flights, as the extraData is also used for filtered flights
                    if (Utils.contains(selectedFlightsIds, extraData?.flightId))
                        for (const tvId in extraData.tvsOccupancyPeriod) {
                            if (!occupancyPeriods[tvId]) {
                                occupancyPeriods[tvId] = new Map<FlightId, TimePeriod>();
                            }
                            occupancyPeriods[tvId].set(extraData.flightId, extraData.tvsOccupancyPeriod[tvId]);
                        }
                });
            }
        }
        return occupancyPeriods;
    };

    refreshFilteredData = () => {
        if (!this.hasFilterEnabled() || !this.state.globalFilterEnabled) {
            // No need to query for selected data - remove filtered data
            this.setState({
                filteredData: undefined,
                selectedFlightsOccupancyPeriods: undefined,
            });
            this.updateFlights(this.enrichedFlights);
            return;
        }

        // We only refresh filtered data when there is a filter, and it is enabled
        const request: DashboardDataRequest = {
            version: this.state?.dashboardData?.version,
            monitoredPeriod: AppContext.getMonitoringPeriod(),
            overlappingFilter: this.getOverlappingFilter(),
            capacityModel: AppContext.getCapacityModel(),
        };
        ServiceFactory.sol49Service()
            .getDashboardFilteredData(request)
            .then(reply => {
                const occupancyPeriods = this.computeSelectedFlightsOccupancyPeriods(reply.data?.flightsExtraData);
                this.setState({
                    filteredData: reply.data,
                    selectedFlightsOccupancyPeriods: occupancyPeriods,
                });

                this.updateFlights(this.enrichedFlights, occupancyPeriods);
            })
            .catch(reason => {
                console.log(reason);
                //AppContext.errorNotifier.notifyCatch(reason.message)
            });
    };

    getSelectedTvs = () => {
        const selectedTvIds = Array.from(this.state.selectedPeriodByTv?.keys());
        const monitoredTvs = this.getMonitoredTvList(this.state.dashboardData);
        return monitoredTvs.filter(monitoredTv => Utils.contains(selectedTvIds, monitoredTv.trafficCounts?.tvId));
    };

    getOverlappingFilter = (): OverlappingFilter => {
        const tvFlightsFilterButtonDetails = this.state.tvFlightsFilterButtonDetails;

        // Override TV => Flights settings, to include selected periods for each TV
        tvFlightsFilterButtonDetails.forwardSettings.config.tvOccupancyPeriods = Utils.mapToObject(
            this.state.selectedPeriodByTv,
            value => {
                // Convert count bar selection periods into counting periods
                // TODO: Deal with Occupancy
                return value && value.map(period => TrafficCountUtils.countingPeriodFor2(period, 20, 60));
            },
        );

        const filterButtonDetails = [tvFlightsFilterButtonDetails];

        return {
            selectedTvs: this.getSelectedTvs().map(monitoredTv =>
                KeyUtils.MONITORED_TV_KEY_PROVIDER.getKey(monitoredTv),
            ),
            selectedMeasures: [],
            selectedFlights: this.state.selectedFlights.map(flight => KeyUtils.FLIGHT_KEY_PROVIDER.getKey(flight)),
            tvsFilters: OverlappingFilterUtils.getOverlappingFilterForEntity(filterButtonDetails, Entity.TV),
            measuresFilters: OverlappingFilterUtils.getOverlappingFilterForEntity(filterButtonDetails, Entity.MEASURE),
            flightsFilters: OverlappingFilterUtils.getOverlappingFilterForEntity(filterButtonDetails, Entity.FLIGHT),
        };
    };

    handleMapSettingsChange = (mapSettings: MapSettings) => {
        this.setState(
            () => {
                //if not showing convection anymore then clea map/data
                if (!mapSettings.showConvection) {
                    return {
                        mapSettings: mapSettings,
                        convectionData: null,
                        // convectionAirspaceDef: null,
                    } as FmpDashboardState;
                }
                return {
                    mapSettings: mapSettings,
                } as FmpDashboardState;
            },
            () => {
                if (this.state.mapSettings.showConvection) {
                    //the perfect implementation would just force this refresh if the show convection have been changed (false -> true)
                    this.refreshDashboardData(this.state.dashboardData.version);
                } else {
                    this.updateConvections(mapSettings);
                }
            },
        );
    };

    handleTextFilterChange = (textFilter: string): void => {
        this.setState({ globalTextFilter: textFilter });
    };

    handleSelectedPeriodByTvChange = (selectedPeriodByTv: Map<string, Array<TimePeriod>>) => {
        const wasSelected = this.state.selectedPeriodByTv.size > 0;
        const isSelected = selectedPeriodByTv.size > 0;

        let tvFlightsFilterButtonDetails = this.state.tvFlightsFilterButtonDetails;

        if (!wasSelected && isSelected) {
            tvFlightsFilterButtonDetails = tvFlightsFilterButtonDetails.copy({
                enabled: true,
                direction: tvFlightsFilterButtonDetails.direction || FilterButtonDirection.FORWARD,
            });
        } else if (wasSelected && !isSelected) {
            const hasSelectedFlights = Utils.isNotEmpty(this.state.selectedFlights);

            tvFlightsFilterButtonDetails = tvFlightsFilterButtonDetails.copy({
                enabled: hasSelectedFlights ? tvFlightsFilterButtonDetails.enabled : undefined,
                direction: hasSelectedFlights ? FilterButtonDirection.BACKWARD : FilterButtonDirection.NONE,
            });
        }

        this.setState(
            {
                selectedPeriodByTv: selectedPeriodByTv,
                tvFlightsFilterButtonDetails: tvFlightsFilterButtonDetails,
            },
            () => this.refreshFilteredData(),
        );
    };

    handleTVSelectionChange = (selectedTvs: Array<ExtendedMonitoredTv>) => {
        // TODO: Handle this case (including select all)
        const selectedPeriodByTv = new Map<string, Array<TimePeriod>>();

        const sectorOpenings = this.state.configuredAirspaces?.flatMap(
            configuredAirspace => configuredAirspace.sectorOpenings,
        );
        selectedTvs.forEach(monitoredTv => {
            const tvSectorOpenings = sectorOpenings.filter(sectorOpening =>
                Utils.contains(
                    sectorOpening.airspaces.map(asRef => asRef.id),
                    monitoredTv.tvRefLocation.refId,
                ),
            );
            if (tvSectorOpenings) {
                tvSectorOpenings.forEach(sectorOpening => {
                    const selectedPeriods = selectedPeriodByTv.get(monitoredTv.refLocation.id) || [];
                    selectedPeriods.push(sectorOpening.timePeriod);
                    selectedPeriodByTv.set(monitoredTv.refLocation.id, selectedPeriods);
                });
            }
        });
        this.handleSelectedPeriodByTvChange(selectedPeriodByTv);

        void this.nmui4dContext?.unityRequests.sendTVsToUnityAsync(selectedTvs);
    };

    handleFlightSelectionChange = (selectedFlights: Array<FlightDto>) => {
        const wasSelected = Utils.isNotEmpty(this.state.selectedFlights);
        const isSelected = Utils.isNotEmpty(selectedFlights);

        let tvFlightsFilterButtonDetails = this.state.tvFlightsFilterButtonDetails;

        if (!wasSelected && isSelected) {
            tvFlightsFilterButtonDetails = tvFlightsFilterButtonDetails.copy({
                enabled: true,
                direction: tvFlightsFilterButtonDetails.direction || FilterButtonDirection.BACKWARD,
            });
        } else if (wasSelected && !isSelected) {
            const hasSelectedTvs = this.state.selectedPeriodByTv.size > 0;

            tvFlightsFilterButtonDetails = tvFlightsFilterButtonDetails.copy({
                enabled: hasSelectedTvs ? tvFlightsFilterButtonDetails.enabled : undefined,
                direction: hasSelectedTvs ? FilterButtonDirection.FORWARD : FilterButtonDirection.NONE,
            });
        }

        const updatedSelectedFlights = TableUtils.getUpdatedMultipleSelection(
            selectedFlights,
            this.state.dashboardData?.flights,
            KeyUtils.FLIGHT_KEY_PROVIDER,
        );

        this.setState(
            {
                selectedFlights: updatedSelectedFlights,
                tvFlightsFilterButtonDetails: tvFlightsFilterButtonDetails,
            },
            () => this.refreshFilteredData(),
        );
    };

    handleCountTypeChanged = (countType: CountType) => {
        this.setState({ countType: countType });
    };

    hasFilterEnabled = () => {
        return (
            this.state.selectedPeriodByTv.size > 0 ||
            Utils.isNotEmpty(this.state.selectedFlights) ||
            Utils.isNotShallowEquals(this.state.flightListFilterSettings, DEFAULT_FLIGHT_LIST_FILTER_SETTINGS) ||
            Utils.isNotEmptyString(this.state.globalTextFilter)
        );
    };

    clearFilters = () => {
        this.setState(
            {
                //selectedTvs: [],
                selectedPeriodByTv: new Map(),
                selectedFlights: [],
                tvFlightsFilterButtonDetails: new OverlappingFilterButtonDetail(
                    Entity.TV,
                    Entity.FLIGHT,
                    DEFAULT_TV_FLIGHTS_SETTINGS,
                    DEFAULT_FLIGHTS_TV_SETTINGS,
                ),
                globalTextFilter: '',
                globalFilterEnabled: true, //reset global filtering toggle
            },
            () => this.refreshFilteredData(),
        );
    };

    handleFlightListFilterSettingsChange = (settings: FlightListFilterSettings) => {
        this.setState({ flightListFilterSettings: settings });
    };

    handleTvFlightsFilterSettingsChange = (settings: TvFlightsFilterSettings) => {
        this.setState(
            {
                tvFlightsFilterButtonDetails: this.state.tvFlightsFilterButtonDetails.copy({
                    forwardSettings: settings,
                }),
            },
            () => this.refreshFilteredData(),
        );
    };
    handleFlightsTvFilterSettingsChange = (settings: FlightsTvFilterSettings) => {
        this.setState(
            {
                tvFlightsFilterButtonDetails: this.state.tvFlightsFilterButtonDetails.copy({
                    backwardSettings: settings,
                }),
            },
            () => this.refreshFilteredData(),
        );
    };
    handleGlobalFilterEnabledChange = (value: boolean) => {
        this.setState({ globalFilterEnabled: value }, () => this.refreshFilteredData());
    };
    handleMeasureSelectionChange = (selectedMeasures: Array<MonitoredMeasure>) => {
        this.setState({
            selectedMeasures: TableUtils.getUpdatedMultipleSelection(
                selectedMeasures,
                this.state.dashboardData.monitoredMeasures,
                KeyUtils.MONITORED_MEASURE_KEY_PROVIDER,
            ),
        });
    };
    handleMeasureSettingsChange = (value: MeasuresSettings) => {
        this.setState({ measureSettings: value });
    };
    getTvFlightFilter = () => {
        const filterButtonDetails = this.state.tvFlightsFilterButtonDetails;

        if (filterButtonDetails.direction === FilterButtonDirection.NONE) return;

        // TODO: have state of the filter changed to checked:false when settings are applied
        const settingsButton =
            filterButtonDetails.direction === FilterButtonDirection.FORWARD ? (
                <TvFlightsFilterSettingsButton
                    settings={filterButtonDetails.getSettings()}
                    onSettingsChange={this.handleTvFlightsFilterSettingsChange}
                />
            ) : (
                <FlightsTvFilterSettingsButton
                    settings={filterButtonDetails.getSettings()}
                    onSettingsChange={this.handleFlightsTvFilterSettingsChange}
                />
            );

        return (
            <FilterComponent
                enabled={filterButtonDetails.enabled && this.state.globalFilterEnabled}
                orientation={DashboardUtils.getFilterOrientation(filterButtonDetails, 'horizontal')}
                onEnabledChange={enabled =>
                    this.setState(
                        {
                            tvFlightsFilterButtonDetails: DashboardUtils.getButtonDetailsOnEnabledUpdate(
                                filterButtonDetails,
                                enabled,
                            ),
                        },
                        () => this.refreshFilteredData(),
                    )
                }
                onOrientationChange={orientation =>
                    this.setState(
                        {
                            tvFlightsFilterButtonDetails: DashboardUtils.getButtonDetailsOnOrientationUpdate(
                                filterButtonDetails,
                                orientation,
                            ),
                        },
                        () => this.refreshFilteredData(),
                    )
                }
                reverseEnabled={
                    filterButtonDetails.direction === FilterButtonDirection.FORWARD
                        ? Utils.isNotEmpty(this.state.selectedFlights)
                        : this.state.selectedPeriodByTv.size > 0
                }
                settingsButton={settingsButton}
            />
        );
    };

    getProposalMeasureIds = memoizeOne((monitoredMcdmMeasures: Array<MonitoredMcdmMeasure>) => {
        // TODO consider "proposalFeedback" state when able to change it
        return monitoredMcdmMeasures
            ?.filter(proposal => proposal.mcdmMeasureTopic.state === McdmState.PROPOSED)
            .map(m => m.measure.measureId.id);
    });

    renderMap = () => {
        return (
            <NetworkMonitoringMap
                currentTime={this.state.currentTime}
                monitoredPeriod={this.state.monitoredPeriod}
                selectedFlights={this.state.selectedFlights}
                selectedTvs={this.state.dashboardData.monitoredTvList}
                monitoredTvs={this.getMonitoredTvList(this.state.dashboardData)}
                tvsOccupancyPeriods={this.state.selectedFlightsOccupancyPeriods}
                visible={true}
                convectionData={this.state.convectionData}
                // airspaceDef={this.state.convectionAirspaceDef}
                mapSettings={this.state.mapSettings}
                onMapSettingsChanged={this.handleMapSettingsChange}
                fullscreenMap={false}
                monitoredMeasures={this.state.dashboardData?.monitoredMeasures}
                selectedMeasures={this.state.selectedMeasures}
                textFilter={this.state.globalTextFilter}
                tvSetMap={(this.state.dashboardData as NetworkDashboardData)?.tvSetMap} //TODO this is right? not quite sure when it returns a NetworkDashboardData object
                dashboardDataVersion={this.state.dashboardData?.version}
                filteredTvIds={this.state.filteredData?.filteredTvs}
                hotspots={[]}
                selectedHotspots={[]}
                regulationOutlook={[]}
                selectedRegulationOutlook={[]}
                onDisplayChanged={this.onDisplayChanged}
                onFlightLevelFilterChanged={this.onFlightLevelFilterChanged}
                onMapRefTimePeriodChanged={this.onMapRefTimePeriodChanged}
            />
        );
    };

    renderMeasures = () => {
        const displayedMeasures = this.state.filteredData?.filteredMeasures
            ? this.state.dashboardData?.monitoredMeasures?.filter(monitoredMeasure => {
                  return Utils.contains(this.state.filteredData?.filteredMeasures, monitoredMeasure.measure.measureId);
              })
            : this.state.dashboardData?.monitoredMeasures;

        return (
            <Measures
                currentTime={this.state.currentTime}
                monitoredPeriod={this.state.dashboardData.period}
                measures={displayedMeasures}
                proposalMeasures={this.getProposalMeasureIds(this.state.dashboardData?.monitoredMcdmMeasures)}
                monitoredTvList={this.getMonitoredTvList(this.state.dashboardData)}
                textFilter={this.state.globalFilterEnabled ? this.state.globalTextFilter : ''}
                selectedMeasures={this.state.selectedMeasures}
                onSelectionChange={this.handleMeasureSelectionChange}
                settings={this.state.measureSettings}
                onMeasureSettingsChange={this.handleMeasureSettingsChange}
            />
        );
    };

    render() {
        if (Utils.isNotExists(this.state.dashboardData)) return null;

        // TODO: Any state change triggers the re-rendering of the filter components, which is not optimal (use memoize-one?)
        const tvFlightsFilterForward =
            this.state.tvFlightsFilterButtonDetails.direction === FilterButtonDirection.FORWARD;

        const nbFiltersOnTv =
            this.state.tvFlightsFilterButtonDetails.direction === FilterButtonDirection.BACKWARD ? 1 : 0;
        const tvFilterApplied =
            nbFiltersOnTv > 0 ? (this.state.selectedPeriodByTv.size === 0 ? 'secondary-tv' : 'tertiary-tv') : 'primary';

        const tvFlightFilterVisible = this.state.tvFlightsFilterButtonDetails.direction !== FilterButtonDirection.NONE;
        const nbFiltersOnFlights =
            this.state.tvFlightsFilterButtonDetails.direction === FilterButtonDirection.FORWARD ? 1 : 0;
        const flightFilterApplied =
            nbFiltersOnFlights > 0 ? (nbFiltersOnFlights === 1 ? 'secondary' : 'tertiary') : 'primary';

        // Filter out flights from the list of filtered flight ids that have been returned
        let displayedFlights;
        if (this.state.filteredData?.filteredFlights) {
            displayedFlights = FlightUtils.getFlights(
                this.state.dashboardData?.flights,
                this.state.filteredData?.filteredFlights,
            );
        } else {
            displayedFlights = this.state.dashboardData?.flights;
        }

        // Get the list of selected TV ids from the selected monitored TVs
        // Entry and Exit columns in the flight list are only displayed when only one single concerned TV is selected
        const showEntryExitColumns = this.state.selectedPeriodByTv.size === 1 && nbFiltersOnFlights > 0;

        // Add extra data that is specific to filters applied, to the flights
        if (this.state.filteredData?.flightsExtraData) {
            this.state.filteredData.flightsExtraData.forEach(flightExtraData => {
                const flight = KeyUtils.getValueFromKey(
                    displayedFlights,
                    flightExtraData.flightId,
                    KeyUtils.FLIGHT_KEY_PROVIDER,
                );
                if (flight) {
                    flight.timeAtReferenceLocationEntry = flightExtraData.timeAtReferenceLocationEntry;
                    flight.timeAtReferenceLocationExit = flightExtraData.timeAtReferenceLocationExit;
                } else {
                    console.log('could not find flight in the data with the following id', flightExtraData.flightId);
                }
            });
        }

        const globalFilterClassName = !this.state.globalFilterEnabled ? ' global-filter-disabled' : '';

        return (
            <Flex className={'dashboard' + globalFilterClassName} direction="horizontal">
                <SvgCommonDefs />
                <Flex className={tvFilterApplied + '-filter'}>
                    <FmpNetworkMonitoring
                        currentTime={this.state.currentTime}
                        monitoredPeriod={this.state.dashboardData?.period}
                        filteredTvIds={this.state.filteredData?.filteredTvs}
                        monitoredTvs={this.getMonitoredTvList(this.state.dashboardData)}
                        monitoredMeasures={this.state.dashboardData?.monitoredMeasures}
                        monitoredMcdmMeasures={this.state.dashboardData?.monitoredMcdmMeasures}
                        coordinationSummaries={this.state.dashboardData?.coordinationSummaries}
                        tvsOccupancyPeriods={this.state.selectedFlightsOccupancyPeriods}
                        configuredAirspaces={this.state.configuredAirspaces}
                        textFilter={this.state.globalTextFilter}
                        onTextFilterChange={this.handleTextFilterChange}
                        selectedTvs={this.getSelectedTvs()}
                        selectedFlights={this.state.selectedFlights}
                        onSelectionChange={this.handleTVSelectionChange}
                        onMapSettingsChanged={this.handleMapSettingsChange}
                        mapSettings={this.state.mapSettings}
                        convectionData={this.state.convectionData}
                        // convectionAirspaceDef={this.state.convectionAirspaceDef}
                        tvMeteoAlerts={this.state.dashboardData?.tvMeteoAlerts}
                        globalFilterEnabled={this.state.globalFilterEnabled}
                        onGlobalFilterEnabledChanged={this.handleGlobalFilterEnabledChange}
                        selectedPeriodByTv={this.state.selectedPeriodByTv}
                        onSelectedPeriodByTvChange={this.handleSelectedPeriodByTvChange}
                        countType={this.state.countType}
                        onCountTypeChanged={this.handleCountTypeChanged}
                        dashboardDataVersion={this.state.dashboardData?.version}
                        measureSettings={this.state.measureSettings}
                    />
                </Flex>
                <Flex direction="vertical" size="1px" style={{ backgroundColor: 'black' }}>
                    <div
                        style={{
                            position: 'relative',
                            height: '100%',
                            zIndex: 10,
                            display: this.hasFilterEnabled() ? 'block' : 'none',
                        }}>
                        {/* Clear all filters button */}
                        {DashboardUtils.renderFilterComponent(
                            <ClearButton onClick={() => this.clearFilters()} style={{ width: '4em' }} />,
                            true,
                            'clear-filter',
                        )}

                        {/* Filter between TVs and Flights, including hexagonal background */}
                        {DashboardUtils.renderFilterComponent(
                            <div
                                className={
                                    tvFlightsFilterForward
                                        ? `${tvFilterApplied}-filter filter-background ltr`
                                        : `${flightFilterApplied}-filter filter-background rtl`
                                }
                            />,
                            tvFlightFilterVisible,
                            tvFlightsFilterForward ? 'tv-flight-filter' : 'flight-tv-filter',
                        )}
                        {DashboardUtils.renderFilterComponent(
                            this.getTvFlightFilter(),
                            tvFlightFilterVisible,
                            tvFlightsFilterForward ? 'tv-flight-filter-btn' : 'flight-tv-filter-btn',
                        )}
                    </div>
                </Flex>
                <Flex direction="vertical" size="35vw">
                    <Flex className="primary-filter" style={{ maxHeight: '50%' }}>
                        <Flex className="map-measures-panel" style={{ padding: '1em 1em 0em 2em' }}>
                            <FlexTabs
                                activeKey={this.state.activeTab}
                                type="card"
                                onTabClick={(key: string) => {
                                    return this.setState({
                                        activeTab: key,
                                    });
                                }}
                                items={[
                                    { key: 'map', label: 'MAP', children: this.renderMap() },
                                    { key: 'measures', label: 'MEASURES', children: this.renderMeasures() },
                                ]}
                            />
                            {/*this.renderMap()*/}
                        </Flex>
                    </Flex>
                    <Flex size="1px" style={{ backgroundColor: 'black' }} />
                    <Flex size="50%">
                        <Flex className={flightFilterApplied + '-filter'}>
                            <Flights
                                flights={displayedFlights}
                                showEntryExitColumns={showEntryExitColumns}
                                selection={this.state.selectedFlights}
                                onSelectionChange={this.handleFlightSelectionChange}
                                filterSettings={this.state.flightListFilterSettings}
                                onFilterSettingsChange={this.handleFlightListFilterSettingsChange}
                            />
                        </Flex>
                    </Flex>
                </Flex>
                <DashboardModal />
            </Flex>
        );
    }
}
